import './AdvancedSearch.module.less';

import {
  type Event,
  type JobType,
  type Property,
  User,
  UserAttributes,
  type UsersFilter,
} from '@kalos/kalos-rpc';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  CardHeader,
  CardTitle,
  cn,
  DataTablePagination,
  SimpleSelect,
  Tabs,
} from '@kalos/ui';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import throttle from 'lodash/throttle';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { RecordTabsList, RecordTabsTrigger } from '../../../components/RecordTabs';
import { currentSearchHref } from '../../../components/searchReturnTo';
import { MONTHS, ROWS_PER_PAGE, USA_STATES_OPTIONS, WORKERS_COMP_CODES } from '../../../constants';
import { useAuth } from '../../../context/AuthContext';
import { queryKeys } from '../../../hooks/react-query/constants';
import { useJobTypesQuery } from '../../../hooks/react-query/useJobTypesQuery';
import { useQuoteLineQuery } from '../../../hooks/react-query/useQuoteLineQuery';
import { useTimesheetDepartmentListQuery } from '../../../hooks/react-query/useTimesheetDepartmentQuery';
import { useUserQuery } from '../../../hooks/react-query/useUserQuery';
import { useMobileDevice } from '../../../hooks/useMedia';
import {
  type EventsFilter,
  MapClientService,
  type PropertiesFilter,
  S3ClientService,
  UserClientService,
} from '../../../tools/helpers';
import { AddServiceCall } from '../../AddServiceCallGeneral/components/AddServiceCall';
import { CustomerEditDialog } from '../../CustomerDetailsNew/components/CustomerEditDialog';
import { Field } from '../Field';
import { FlatRateSheet } from '../FlatRate';
import { Form } from '../Form';
import { Modal } from '../Modal';
import { type Schema } from '../PlainForm';
import { type Kind } from './constants';
import { ContractsSearch } from './ContractsSearch/ContractsSearch';
import { CustomersSearch } from './CustomerSearch/CustomersSearch';
import { EmployeesSearch } from './EmployeesSearch/EmployeesSearch';
import { EMPLOYEE_SECTORS } from './EmployeesSearch/subcomponents/EmployeeForm.hooks';
import { useListPageAndSort } from './listState';
import {
  AdvancedSearchListContext,
  type AdvancedSearchListState,
  useAdvancedSearchPaginationStore,
} from './paginationStore';
import { PropertiesSearch } from './PropertiesSearch/PropertiesSearch';
import { ServiceCallsSearch } from './ServiceCallsSearch/ServiceCallsSearch';

export interface AdvancedSearchProps {
  title: string;
  kinds: Kind[];
  deletableEvents?: boolean;
  editableCustomers?: boolean;
  deletableCustomers?: boolean;
  showRecentServiceCallsForEmployee?: boolean;
  onChangeKindSelection?: (kind: Kind) => void;
  printableEmployees?: boolean;
  editableProperties?: boolean;
  deletableProperties?: boolean;
  eventsWithAccounting?: boolean;
  eventsWithAdd?: boolean;
  className?: string;
  onSelectEvent?: (event: Event) => void;
  onSelectProperty?: (property: Property) => void;
  disableParamsSync?: boolean;
  onClose?: () => void;
  omitArchivedJobs?: boolean;
  showExport?: boolean;
  headerActions?: React.ReactNode;
}

export type SearchForm = EventsFilter | UsersFilter | PropertiesFilter;

/** The kinds that make up the customer, property and contract directory. */
const DIRECTORY_KINDS: Kind[] = ['customers', 'properties', 'contracts'];

/** What the kind switch says on phones, in place of the raw kind names. */
const KIND_LABELS: Record<Kind, string> = {
  customers: 'Customers',
  properties: 'Properties',
  contracts: 'Contracts',
  serviceCalls: 'Jobs',
  employees: 'Employees',
};

const ACCOUNTING = 'Accounting';
const SERVICE = 'Service';

export const AdvancedSearch: FC<AdvancedSearchProps> = ({
  title,
  kinds,
  deletableEvents,
  editableCustomers,
  deletableCustomers,
  showRecentServiceCallsForEmployee,
  printableEmployees = false,
  editableProperties,
  deletableProperties,
  eventsWithAccounting = false,
  onChangeKindSelection,
  eventsWithAdd = false,
  className,
  onSelectEvent,
  onSelectProperty,
  disableParamsSync,
  onClose,
  omitArchivedJobs,
  showExport = false,
  headerActions,
}) => {
  const auth = useAuth();
  const loggedUserId = auth.user.id;

  const userQuery = useUserQuery({
    filters: {
      id: loggedUserId || 0,
    },
    enabled: typeof loggedUserId === 'number',
    select(data) {
      return {
        ...data,
        isManager: !!(
          data.isAdmin ||
          data.permissionGroups.filter((el) => el.name == 'Manager' || el.name == 'Payroll')
            .length > 0
        ),
        canAddEmployee: !!(
          data.permissionGroups.filter(
            (el) =>
              el.name == 'HR' ||
              el.name == 'Payroll' ||
              el.name == 'LimitedEmployeeDirectoryAccess',
          ).length > 0
        ),
      };
    },
  });

  // Add jobsQuery
  const jobsQuery = useJobTypesQuery({
    select: useCallback((data: JobType[]) => {
      return data.map((item: JobType) => ({
        value: item.id.toString(),
        label: item.name || '',
      }));
    }, []),
  });

  const isHR = useMemo(() => {
    return (
      userQuery.data &&
      userQuery.data.permissionGroups.filter((el) => el.name == 'HR' || el.name == 'Payroll')
        .length > 0
    );
  }, [userQuery.data]);

  const [flatRateIsOpen, setFlatRateIsOpen] = useState<boolean>(false);

  const flatRateQuery = useQuoteLineQuery({
    filters: {
      isActive: 1,
      isFlatrate: '1',
      pageNumber: 0,
      withoutLimit: true,
    },
    enabled: eventsWithAdd,
  });

  const { kind: kindFromSearch, ...rest } = useSearch({ strict: false });

  const kind = useMemo(() => {
    if (kindFromSearch) {
      return kinds.includes(kindFromSearch) ? (kindFromSearch as Kind) : kinds[0];
    }
    return kinds[0];
  }, [kinds, kindFromSearch]);
  const navigate = useNavigate();

  // The directory (customers, properties, contracts) keeps its page and sort in the URL, so a
  // reload or a shared link keeps them. Pickers and mixed searches keep the shared store.
  const usesUrlListState =
    !disableParamsSync &&
    !onSelectProperty &&
    !onSelectEvent &&
    kinds.every((searchKind) => DIRECTORY_KINDS.includes(searchKind));
  const urlList = useListPageAndSort({ syncToUrl: true });
  // Unknown until the first load. The pager isn't shown before then: it clamps the page to its
  // page count, which would throw away a page from the URL.
  const [urlTotalEntries, setUrlTotalEntries] = useState<number | null>(null);
  const listState = useMemo<AdvancedSearchListState | null>(
    () =>
      usesUrlListState
        ? {
            page: urlList.page,
            setPage: urlList.setPage,
            totalEntries: urlTotalEntries ?? 0,
            setTotalEntriesCount: setUrlTotalEntries,
            sort: urlList.sort,
            setSort: urlList.setSort,
          }
        : null,
    [
      usesUrlListState,
      urlList.page,
      urlList.setPage,
      urlList.sort,
      urlList.setSort,
      urlTotalEntries,
    ],
  );

  const page = useAdvancedSearchPaginationStore((state) => state.page);
  const setPage = useAdvancedSearchPaginationStore((state) => state.setPage);
  // To solve https://github.com/kalos-software/kalos-frontend/issues/546
  const [_page, _setPage] = useState(0);

  // synchronize global page (that is used in query) with pagination state if it was changed from outside
  useEffect(() => {
    _setPage(page);
  }, [page]);

  const totalEntriesCount = useAdvancedSearchPaginationStore((state) => state.totalEntries);

  const [accounting, setAccounting] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const reloadServiceCallQuery = useCallback(() => {
    queryClient.refetchQueries({ queryKey: [queryKeys.events.root] });
  }, [queryClient]);

  const [pendingEventAdding, setPendingEventAdding] = useState<boolean>(false);
  const [pendingCustomerEditing, setPendingCustomerEditing] = useState<User>();

  const isAdmin = !!userQuery.data?.isAdmin;
  // const canAddEmployee = !!userQuery.data?.canAddEmployee;
  const handleInternalPageChange = useMemo(
    () =>
      throttle(
        (newPageValue: number) => {
          setPage(newPageValue);
        },
        2500,
        {
          leading: true,
          trailing: true,
        },
      ),
    [setPage],
  );

  const handlePageChange = useCallback(
    (updater: React.SetStateAction<number>) => {
      _setPage((prevValue) => {
        const newValue = typeof updater === 'function' ? updater(prevValue) : updater;
        handleInternalPageChange(newValue);
        return newValue;
      });
    },
    [handleInternalPageChange],
  );

  const handlekindChange = useCallback(
    (kind: Kind) => {
      console.log('kind', kind);
      // A new kind starts on its first page, in its own default order.
      navigate({
        to: '.',
        search: {
          ...rest,
          kind: kind,
          page: undefined,
          sort: undefined,
        },
      });
      setPage(0);
    },
    [navigate, rest, setPage],
  );

  const handlePendingEventAddingToggle = useCallback(
    (pendingEventAdding: boolean) => () => setPendingEventAdding(pendingEventAdding),
    [setPendingEventAdding],
  );

  const handlePendingCustomerEditingToggle = useCallback(
    (pendingCustomerEditing?: User) => () => setPendingCustomerEditing(pendingCustomerEditing),
    [setPendingCustomerEditing],
  );

  const onSaveCustomer = useCallback(() => {
    setPendingCustomerEditing(undefined);

    queryClient.refetchQueries({ queryKey: [queryKeys.user.root, queryKeys.user.list] });
  }, [queryClient]);

  const handleAccountingToggle = useCallback(
    () => setAccounting(!accounting),
    [accounting, setAccounting],
  );

  const [newEmployeeEditing, setNewEmployeeEditing] = useState<User>();
  const [newUserAttributes, setNewUserAttributes] = useState<UserAttributes>(
    UserAttributes.create(),
  );
  // const createNewEmployee = useCallback(async () => {
  //   const newEmployee = User.create();
  //   newEmployee.hireDate = new Date().toISOString();
  //   newEmployee.isEmployee = 1;
  //   newEmployee.userAttributes = await UserClientService.GetUserAttributes(
  //     UserAttributes.create({
  //       userId: newEmployee.id,
  //       fieldMask: ['UserId'],
  //     }),
  //   );
  //   setNewUserAttributes(newEmployee.userAttributes);
  //   setNewEmployeeEditing(newEmployee);
  // }, []);

  const limitedAccess =
    userQuery.data?.permissionGroups.find(
      (permission) => permission.name === 'LimitedEmployeeDirectoryAccess',
    ) != undefined;
  const departmentsQuery = useTimesheetDepartmentListQuery({ filter: { isActive: 1 } });

  const [employeeUploadedPhoto, setEmployeeUploadedPhoto] = useState<string>('');
  const handleEmployeePhotoUpload = useCallback(
    (file: string | ArrayBuffer | null) => {
      if (!file) return;
      setEmployeeUploadedPhoto(file as string);
    },
    [setEmployeeUploadedPhoto],
  );

  const handleDeleteEmployeePhoto = useCallback(() => {
    if (!newEmployeeEditing) return;
    setEmployeeUploadedPhoto('');
  }, [newEmployeeEditing]);

  const onNewEmployeeClose = useCallback(() => {
    setNewEmployeeEditing(undefined);
    setEmployeeUploadedPhoto('');
  }, []);

  const makeSchemaEmployeesCreate = useCallback(
    (entry: User): Schema<User> => {
      return [
        [{ name: 'isEmployee', type: 'hidden' }],
        [{ headline: true, label: 'Personal Details' }],
        [
          {
            name: 'firstname',
            label: 'First Name',
            required: true,
            disabled: entry.id !== 0 && !isAdmin && userQuery.data?.id != entry.id,
          },
          {
            name: 'lastname',
            label: 'Last Name',
            required: true,
            disabled: entry.id !== 0 && !isAdmin && userQuery.data?.id != entry.id,
          },
        ],
        [
          {
            name: 'address',
            label: 'Street Address',
            required: true,
            multiline: true,
            disabled: entry.id !== 0 && !isAdmin && userQuery.data?.id != entry.id && limitedAccess,
          },
          {
            name: 'city',
            required: true,
            label: 'City',
            disabled: entry.id !== 0 && !isAdmin && userQuery.data?.id != entry.id && limitedAccess,
          },
          {
            name: 'zip',
            required: true,
            label: 'Zipcode',
            disabled: entry.id !== 0 && !isAdmin && userQuery.data?.id != entry.id && limitedAccess,
          },
          {
            name: 'state',
            label: 'State',
            required: true,
            options: USA_STATES_OPTIONS,
            disabled: entry.id !== 0 && !isAdmin && userQuery.data?.id != entry.id && limitedAccess,
          },
        ],
        [
          {
            name: 'empTitle',
            label: 'Title',
            disabled: !isAdmin && limitedAccess,
          },
          {
            name: 'employeeDepartmentID',
            label: 'Department',
            options:
              departmentsQuery.data?.results.map((d) => ({
                label: `${d.value} - ${d.description}`,
                value: d.id,
              })) ?? [],
            required: true,
            disabled: !isHR && !!newEmployeeEditing && !!newEmployeeEditing.id,
          },
          {
            name: 'managedBy',
            label: 'Assigned Supervisor',
            type: 'technician',
          },
          {
            name: 'empBadgeId',
            label: 'Workers Comp Code',
            options: WORKERS_COMP_CODES,
            type: 'number',
            disabled: !isHR && entry.id == 0,
          },
        ],
        [
          {
            name: 'phone',
            label: 'Primary Phone',
            required: true,
            disabled: entry.id !== 0 && !isAdmin && userQuery.data?.id != entry.id && limitedAccess,
          },
          {
            name: 'cellphone',
            label: 'Cell Phone',
            disabled: entry.id !== 0 && !isAdmin && userQuery.data?.id != entry.id && limitedAccess,
          },
          {
            name: 'ext',
            label: 'Ext',
            disabled: entry.id !== 0 && !isAdmin && userQuery.data?.id != entry.id && limitedAccess,
          },
          {
            name: 'toolFund',
            label: 'Tool Fund Allowance',
            disabled: !isAdmin,
          },
        ],
        [
          {
            name: 'email',
            label: 'Email',
            disabled: entry.id !== 0 && !isAdmin && userQuery.data?.id != entry.id,
            required: true,
            validationOnSave(value) {
              if (value.trim().replaceAll(' ', '').length <= 0) return 'This field is required';
              else if (!/^\S+@\S+\.\S+$/.test(value)) return 'Please write correct email address';
              return '';
            },
          },
          {
            name: 'phoneEmail',
            label: 'Email-to-SMS',
            disabled: entry.id !== 0 && !isAdmin && userQuery.data?.id != entry.id,
          },
          {},
          {},
        ],
        [{ headline: true, label: 'Employee Permission Details' }],
        [
          {
            name: 'serviceCalls',
            label: 'Runs Jobs',
            type: 'checkbox',
            disabled: !isAdmin,
          },
          {
            name: 'isAdmin',
            label: 'Admin Menu Rights',
            type: 'checkbox',
            disabled: !isAdmin,
          },
          // {
          //   name: 'isAdmin', // FIXME include_in_pdf_list
          //   label: 'Add To Directory PDF',
          //   type: 'checkbox',
          // },
          // {
          //   name: 'isAdmin', // FIXME user_pwreset
          //   label: 'Force P/W Reset',
          //   type: 'checkbox',
          // },
          {
            name: 'paidServiceCallStatus',
            label: '"Paid" Job Status',
            type: 'checkbox',
            disabled: !isAdmin,
          },
          {
            name: 'showBilling',
            label: 'Show billing to user',
            type: 'checkbox',
            disabled: !isAdmin,
          },
        ],
        [
          {
            name: 'isOfficeStaff',
            label: 'Office Staff',
            type: 'checkbox',
            disabled: !isAdmin,
          },
          {
            name: 'isHvacTech',
            label: 'Display On Dispatch?',
            type: 'checkbox',
            disabled: !isAdmin,
          },
          {
            name: 'techAssist',
            label: 'Tech Assist',
            type: 'checkbox',
            disabled: !isAdmin,
          },
          {
            name: 'userSector',
            label: 'Market Sector',
            type: 'number',
            options: EMPLOYEE_SECTORS.map((option: { label: string; value: number }) => ({
              label: option.label,
              value: option.value,
            })),
            disabled: !isAdmin && !limitedAccess && !isHR,
          },
          {
            content: (
              <FormControl sx={{ minWidth: 170 }}>
                <InputLabel>Job Types</InputLabel>
                <Select
                  label="Job Types"
                  multiple
                  disabled={!isAdmin && !limitedAccess && !isHR}
                  value={entry.userJobType ? entry.userJobType.split(',').filter(Boolean) : []}
                  onChange={(event) => {
                    const values = event.target.value as string[];
                    if (newEmployeeEditing) {
                      const updatedEmployee = User.clone(newEmployeeEditing);
                      updatedEmployee.userJobType = values.join(',');
                      setNewEmployeeEditing(updatedEmployee);
                    }
                  }}
                >
                  {jobsQuery.data?.map((job) => (
                    <MenuItem key={job.value} value={job.value}>
                      {job.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ),
          },
          {},
        ],

        [{ headline: true, label: 'Kalos Special Features' }],
        [
          {
            name: 'isColorMute',
            label: 'Color Mute [2017]',
            type: 'checkbox',
            disabled: userQuery.data?.id != entry.id,
          },
          // {
          //   name: 'isAdmin', // FIXME admin_matrics
          //   label: 'Admin Metrics',
          //   type: 'checkbox',
          // },
          // {
          //   name: 'isAdmin', // FIXME tech_matrics
          //   label: 'Tech Metrics',
          //   type: 'checkbox',
          // },
        ],
        // [
        //   {
        //     name: 'isAdmin', // FIXME install_matrics
        //     label: 'Install Metrics',
        //     type: 'checkbox',
        //   },
        //   {
        //     name: 'isAdmin', // FIXME garage_door_matrics
        //     label: 'Garage Door Metrics',
        //     type: 'checkbox',
        //   },
        //   {
        //     name: 'isAdmin', // FIXME refrigeration_matrics
        //     label: 'Refrigeration Metrics',
        //     type: 'checkbox',
        //   },
        // ],
        // [
        //   {
        //     name: 'isAdmin', // FIXME electrician_matrics
        //     label: 'Electrician Metrics',
        //     type: 'checkbox',
        //   },
        //   {},
        //   {},
        // ],
        [{ headline: true, label: 'Paid Time-Off' }],
        [
          {
            name: 'hireDate',
            label: 'Hire Date',
            type: 'mui-date',
            disabled: !!userQuery.data?.permissionGroups.find((el) => el.name == 'HR'),
          },
          // {
          //   // name: 'userAttributes',
          //   label: 'Override Hire Month',
          //   options: MONTHS.map((month, index) => ({
          //     label: month,
          //     value: month,
          //     key: index
          //   })),
          //   defaultValue: entry.userAttributes?.overrideMonth,
          // },
          {
            content: (
              <FormControl sx={{ minWidth: 170 }}>
                <InputLabel>Override Hire Month</InputLabel>
                <Select
                  label={'Override Hire Month'}
                  disabled={true}
                  defaultValue={entry.userAttributes?.overrideMonth}
                  onChange={(data) => {
                    const userAtt = UserAttributes.clone(newUserAttributes);
                    if (userAtt.userId === 0) {
                      userAtt.userId = entry.id;
                    }
                    userAtt.overrideMonth = Number(data.target.value);
                    if (!userAtt.fieldMask.includes('UserId')) {
                      userAtt.fieldMask.push('UserId');
                    }
                    if (!userAtt.fieldMask.includes('OverrideMonth')) {
                      userAtt.fieldMask.push('OverrideMonth');
                    }
                    setNewUserAttributes(userAtt);
                  }}
                >
                  {MONTHS.map((month, index) => {
                    return (
                      <MenuItem value={index + 1} key={index + 1}>
                        {month}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            ),
          },
          {
            content: (
              <Field
                label="Currently Allocated PTO"
                type="number"
                name="PTO Value"
                disabled={true}
                value={0}
              />
            ),
          },
          /*{
        name: 'currentPtoAmount',
        label: 'Current Available PTO',
        type: 'number',
        disabled: true,
      }*/
        ],
        // [{ headline: true, label: 'Dispatch Mode Permission' }],
        // [
        //   {
        //     name: 'isAdmin', // FIXME can_access_dispatch_mode
        //     label: 'Access Dispatch Mode',
        //     type: 'checkbox',
        //   },
        //   {
        //     name: 'isAdmin', // FIXME access_dismiss_employee
        //     label: 'Can Dismiss Employee',
        //     type: 'checkbox',
        //   },
        //   {
        //     name: 'isAdmin', // FIXME is_training_admin
        //     label: 'Training Admin',
        //     type: 'checkbox',
        //   },
        // ],
        [{ headline: true, label: 'Photo' }],
        [
          {
            content: (
              <Avatar className="h-40 w-40">
                <AvatarImage className="object-cover" src={employeeUploadedPhoto || entry.image} />
                <AvatarFallback>{`${entry.firstname.at(0)?.toUpperCase() || ''} ${
                  entry.lastname.at(0)?.toUpperCase() || ''
                }`}</AvatarFallback>
              </Avatar>
            ),
          },
          {
            name: 'image',
            type: 'file',
            withDeleteButton: true,
            onDeleteClick: handleDeleteEmployeePhoto,
            label: 'Photo',
            actionsInLabel: true,
            onFileLoad: handleEmployeePhotoUpload,
          },
          {},
          {},
        ],
      ];
    },
    [
      departmentsQuery.data?.results,
      employeeUploadedPhoto,
      handleDeleteEmployeePhoto,
      handleEmployeePhotoUpload,
      isAdmin,
      jobsQuery.data,
      isHR,
      limitedAccess,
      newEmployeeEditing,
      newUserAttributes,
      userQuery.data?.id,
      userQuery.data?.permissionGroups,
    ],
  );

  const [saving, setSaving] = useState<boolean>(false);
  const onSaveEmployee = useCallback(
    async (data: User) => {
      if (newEmployeeEditing) {
        setSaving(true);

        if (employeeUploadedPhoto) {
          await S3ClientService.uploadFileToS3Bucket(
            data.image,
            employeeUploadedPhoto,
            'kalos-employee-images',
          );
        }
        const newData = User.clone(data);

        // Make sure we save the userJobType that was set via the Select component
        newData.userJobType = newEmployeeEditing.userJobType;

        const address = newData.address;
        const city = newData.city;
        const addressState = newData.state;
        const zip = newData.zip;
        const geo = await MapClientService.loadGeoLocationByAddress(
          `${address}, ${city}, ${addressState} ${zip}`,
        );
        if (geo) {
          newData.geolocationLat = geo.geolocationLat;
          newData.geolocationLng = geo.geolocationLng;
        }
        if (newData.fieldMask.length > 0) {
          await UserClientService.saveUser(newData, newEmployeeEditing.id);
          queryClient.invalidateQueries({ queryKey: [queryKeys.user.root, queryKeys.user.list] });
        }
        if (newUserAttributes.fieldMask.length > 0) {
          try {
            const currentUserAttributes = await UserClientService.GetUserAttributes(
              UserAttributes.create({
                userId: newUserAttributes.userId,
                fieldMask: ['UserId'],
              }),
            );
            if (currentUserAttributes.id !== 0) {
              await UserClientService.UpdateUserAttributes(newUserAttributes);
            } else {
              await UserClientService.CreateUserAttributes(newUserAttributes);
            }
          } catch (err) {
            console.error(err);
          }
        }
        setNewEmployeeEditing(undefined);
        setNewUserAttributes(UserAttributes.create());
        setSaving(false);
        queryClient.invalidateQueries({ queryKey: [queryKeys.user.root] });
      }
    },
    [employeeUploadedPhoto, newEmployeeEditing, newUserAttributes, queryClient],
  );

  const headerRef = useRef<HTMLDivElement>(null);
  const isMobileDevice = useMobileDevice();

  const kindResults = (
    <AdvancedSearchListContext value={listState}>
      <div className="w-full flex-1 items-start">
        {kind === 'serviceCalls' && (
          <ServiceCallsSearch
            accounting={accounting}
            showRecentServiceCallsForEmployee={showRecentServiceCallsForEmployee}
            deletableEvents={deletableEvents}
            onClose={onClose}
            showJob
            onSelectEvent={onSelectEvent}
            omitArchivedJobs={omitArchivedJobs}
            showExport={showExport}
          />
        )}
        {kind === 'employees' && <EmployeesSearch printableEmployees={printableEmployees} />}
        {kind === 'customers' && (
          <CustomersSearch
            deletableCustomers={deletableCustomers}
            editableCustomers={editableCustomers}
          />
        )}
        {kind === 'properties' && (
          <PropertiesSearch
            showPagination={false}
            onSelectProperty={onSelectProperty}
            deletableProperties={deletableProperties}
            editableProperties={editableProperties}
            disableParamsSync={disableParamsSync}
          />
        )}
        {kind === 'contracts' && <ContractsSearch />}
      </div>
    </AdvancedSearchListContext>
  );

  const customerCreateDialog = pendingCustomerEditing && (
    <CustomerEditDialog
      open
      mode="create"
      customer={pendingCustomerEditing}
      groupLinks={[]}
      onClose={handlePendingCustomerEditingToggle(undefined)}
      onSave={onSaveCustomer}
    />
  );

  // The directory on phones: the title and Add New Customer, the kind as labelled tabs, then the
  // list, which brings its own search, filters and count. No page buttons: the list loads more as
  // it scrolls.
  if (isMobileDevice && DIRECTORY_KINDS.includes(kind)) {
    return (
      <div className={cn('bg-background min-h-full', className)}>
        <main className="mx-auto flex w-full max-w-2xl flex-col gap-3 py-4">
          <div className="flex items-center justify-between gap-3 px-4">
            <h1 className="text-xl font-bold">{title}</h1>
            <div className="flex items-center gap-2">
              {headerActions}
              {kinds.includes('customers') && (
                // On phones, the guided Add Customer page; it opens the new customer after.
                <Button
                  onClick={() =>
                    navigate({
                      to: '/customers/add',
                      search: { returnTo: currentSearchHref() },
                    })
                  }
                >
                  Add New Customer
                </Button>
              )}
              {onClose && (
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
              )}
            </div>
          </div>
          {kinds.length > 1 && (
            <Tabs
              value={kind}
              onValueChange={(value) => {
                handlekindChange(value as Kind);
                onChangeKindSelection?.(value as Kind);
              }}
              className="px-4"
            >
              <RecordTabsList aria-label="Search for">
                {kinds.map((searchKind) => (
                  <RecordTabsTrigger key={searchKind} value={searchKind}>
                    {KIND_LABELS[searchKind]}
                  </RecordTabsTrigger>
                ))}
              </RecordTabsList>
            </Tabs>
          )}
          {kindResults}
        </main>
      </div>
    );
  }

  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader className="bg-background/95 supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50 col-span-2 w-full space-y-2 border-b backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-3 md:flex-nowrap">
          <div
            ref={headerRef}
            className="xs:justify-between xs:flex-nowrap flex flex-wrap items-center justify-center gap-2 md:w-max md:justify-start md:gap-4"
          >
            <div className="flex items-center">
              <CardTitle className="text-xl tracking-wide">{title}</CardTitle>
              {(!listState || urlTotalEntries !== null) && (
                <DataTablePagination
                  pageCount={Math.ceil(
                    (listState ? listState.totalEntries : totalEntriesCount) / ROWS_PER_PAGE,
                  )}
                  currentPage={listState ? listState.page : _page}
                  setPage={listState ? listState.setPage : handlePageChange}
                />
              )}
            </div>
            <p className="text-sm">
              Total Entries count: {listState ? listState.totalEntries : totalEntriesCount}
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 md:w-max md:flex-nowrap ">
            {headerActions}
            {kinds.includes('customers') && (
              <Button
                onClick={handlePendingCustomerEditingToggle(User.create({ billingTerms: 'COD' }))}
              >
                Add New Customer
              </Button>
            )}
            {eventsWithAccounting && (
              <Button onClick={handleAccountingToggle}>{accounting ? SERVICE : ACCOUNTING}</Button>
            )}
            {eventsWithAdd && (
              <>
                <Button onClick={() => setPendingEventAdding(true)}>Add Job</Button>
                <Button onClick={() => setFlatRateIsOpen(true)}>View Flat Rate</Button>
              </>
            )}
            {onClose && <Button onClick={onClose}>Close</Button>}
          </div>
        </div>
        {kinds.length > 1 && (
          // <div className='p-4'>
          <SimpleSelect
            selectedValue={kind}
            values={kinds}
            onChange={(value) => {
              handlekindChange(value);
              if (onChangeKindSelection) {
                onChangeKindSelection(value);
              }
            }}
          />
          // </div>
        )}
      </CardHeader>
      {kindResults}
      {flatRateIsOpen && flatRateQuery.isSuccess && flatRateQuery.data?.results && (
        <Modal
          open
          fullScreen
          onClose={() => setFlatRateIsOpen(false)}
          className="Modal-Remove-Scroll"
        >
          <FlatRateSheet onClose={() => setFlatRateIsOpen(false)} />
        </Modal>
      )}
      {pendingEventAdding && (
        <Modal
          open
          onClose={handlePendingEventAddingToggle(false)}
          fullScreen
          className="Modal-Remove-Scroll"
        >
          <AddServiceCall
            onClose={handlePendingEventAddingToggle(false)}
            onSave={reloadServiceCallQuery}
          />
        </Modal>
      )}
      {customerCreateDialog}
      {newEmployeeEditing && (
        <Modal
          open
          onClose={() => setNewEmployeeEditing(undefined)}
          fullScreen
          className="Modal-Remove-Scroll"
        >
          <Form
            title={`Add Employee`}
            schema={makeSchemaEmployeesCreate(newEmployeeEditing)}
            data={newEmployeeEditing}
            onClose={onNewEmployeeClose}
            onSave={onSaveEmployee}
            disabled={saving}
            stickySectionBar
          />
        </Modal>
      )}
    </Card>
  );
};
